import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { money, when } from "@/lib/rides";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const TX_LABEL = { CREDIT_PURCHASE: "Purchase", CREDIT_USED: "Used on ride", CREDIT_REFUND: "Refund", CREDIT_ADJUSTMENT: "Adjustment",
  CREDIT_PROMOTIONAL: "Promotional", CREDIT_EXPIRED: "Expired", CREDIT_VOID: "Voided" };

export const useWallet = () => useQuery({ queryKey: ["credit"], queryFn: () => api.get("/credit").then((r) => r.data) });

export const Stat = ({ label, value, testid }) => (
  <div className="rounded-xl bg-muted p-2.5"><p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p><p className="font-display text-lg font-bold" data-testid={testid}>{money(value)}</p></div>
);

export function Ledger({ rows }) {
  if (!rows?.length) return <p className="text-sm text-muted-foreground">No transactions yet.</p>;
  return (
    <div className="max-h-64 space-y-1.5 overflow-y-auto" data-testid="credit-ledger">
      {rows.map((e) => (
        <div key={e.id} className="flex justify-between gap-2 border-b border-white/5 pb-1.5 text-xs" data-testid="credit-ledger-row">
          <span className="min-w-0"><span className="font-semibold">{TX_LABEL[e.transaction_type]}</span> · {e.description}{e.reason ? ` · ${e.reason}` : ""}<span className="block text-muted-foreground">{when(e.created_at)} · balance {money(e.balance_after)}</span></span>
          <span className={`shrink-0 font-mono font-semibold ${e.amount < 0 ? "text-destructive" : "text-neon-lime"}`}>{e.amount < 0 ? "-" : "+"}{money(Math.abs(e.amount))}</span>
        </div>
      ))}
    </div>
  );
}

export function AddCredit({ presets, onDone }) {
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const buy = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/credit/purchases", { amount: Number(amount) });
      toast.success(`${money(data.amount)} purchase started`, { description: data.instructions });
      setAmount("");
      onDone();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2" data-testid="add-credit">
      <p className="text-sm font-semibold">Add Ride Credit</p>
      <div className="flex flex-wrap gap-2">
        {presets.map((p) => (
          <button key={p} type="button" onClick={() => setAmount(String(p))} data-testid={`credit-preset-${p}`}
            className={`rounded-full border px-3 py-1.5 text-sm font-semibold ${Number(amount) === p ? "border-primary bg-primary text-primary-foreground" : "border-white/15"}`}>${p}</button>
        ))}
      </div>
      <div className="flex gap-2">
        <Input type="number" min="5" max="1000" placeholder="Custom amount" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-10 rounded-full" data-testid="credit-custom-input" />
        <Button onClick={buy} disabled={busy || !(Number(amount) >= 5)} className="h-10 rounded-full" data-testid="credit-buy-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}</Button>
      </div>
      <p className="text-xs text-muted-foreground">Pay Scoopz by cash or card. Credit appears once Scoopz confirms your payment.</p>
    </div>
  );
}

export function CreditDialog({ open, onOpenChange, startAdding }) {
  const qc = useQueryClient();
  const { data: w } = useWallet();
  const cancel = async (id) => {
    await api.post(`/credit/purchases/${id}/cancel`).catch((e) => toast.error(formatApiError(e)));
    qc.invalidateQueries({ queryKey: ["credit"] });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="credit-dialog">
        <DialogHeader><DialogTitle className="font-display">Scoopz Ride Credit</DialogTitle></DialogHeader>
        {!w ? <Loader2 className="h-5 w-5 animate-spin" /> : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              <Stat label="Available" value={w.available} testid="credit-available" />
              <Stat label="Reserved" value={w.reserved} testid="credit-reserved" />
              <Stat label="Pending" value={w.pending_credit} testid="credit-pending" />
              <Stat label="Added" value={w.added} testid="credit-added" />
              <Stat label="Used" value={w.used} testid="credit-used" />
              <Stat label="Refunded" value={w.refunded} testid="credit-refunded" />
            </div>
            {w.pending_purchases.map((p) => (
              <div key={p.id} className="flex items-center justify-between rounded-xl border border-amber-400/40 px-3 py-2 text-xs" data-testid="credit-pending-purchase">
                <span>{money(p.amount)} purchase · waiting for Scoopz to confirm payment</span>
                <button type="button" className="font-semibold underline" onClick={() => cancel(p.id)} data-testid={`credit-cancel-purchase-${p.id}`}>Cancel</button>
              </div>
            ))}
            <AddCredit presets={w.presets} onDone={() => qc.invalidateQueries({ queryKey: ["credit"] })} key={startAdding ? "a" : "b"} />
            <div><p className="mb-2 text-sm font-semibold">Transaction history</p><Ledger rows={w.ledger} /></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function CreditCard() {
  const { data: w } = useWallet();
  const [open, setOpen] = useState(false);
  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl border border-neon-lime/30 bg-card p-3" data-testid="credit-card">
      <div className="flex items-center gap-3">
        <Wallet className="h-5 w-5 text-neon-lime" />
        <div><p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Scoopz Ride Credit</p>
          <p className="font-display text-xl font-bold" data-testid="credit-card-balance">{money(w?.available ?? 0)}</p></div>
      </div>
      <div className="flex gap-1.5">
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => setOpen(true)} data-testid="credit-add-button"><Plus className="mr-1 h-3.5 w-3.5" />Add</Button>
        <Button size="sm" variant="ghost" className="rounded-full" onClick={() => setOpen(true)} data-testid="credit-view-button">View</Button>
      </div>
      <CreditDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
